import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Briefcase, ChevronRight, MessageCircle, Trash2, RotateCcw } from "lucide-react";
import { getToken, fetchJobs, dismissJob, restoreJob, formatEuro } from "@/lib/mastroAuth";
import { StatusBadge } from "@/components/StatusBadge";
import { ConfirmDismissDialog } from "@/components/ConfirmDismissDialog";
import { JOBS } from "@/constants/testIds";

export default function Lavori() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("ACTIVE");
  const [confirmJob, setConfirmJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const load = () => fetchJobs(getToken(), true).then(setData).catch(() => setData(null));

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  const onConfirmDismiss = async () => {
    setBusy(true);
    try {
      await dismissJob(getToken(), confirmJob.id);
      await load();
      setConfirmJob(null);
    } catch {
      // keep dialog open; nothing mutated server-side on failure
    } finally {
      setBusy(false);
    }
  };

  const onRestore = async (jobId) => {
    setRestoringId(jobId);
    try {
      await restoreJob(getToken(), jobId);
      await load();
    } finally {
      setRestoringId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-neutral-500">
        <Loader2 className="h-5 w-5 animate-spin" /> Caricamento…
      </div>
    );
  }

  const TABS = [
    { key: "ACTIVE", label: "Attivi" },
    { key: "COMPLETED", label: "Completati" },
    { key: "ARCHIVED", label: "Archiviati" },
    { key: "DISMISSED", label: "Eliminati" },
  ];
  const allJobs = data?.jobs || [];
  const visibleJobs = allJobs.filter((j) => !j.dismissed);
  const dismissedJobs = allJobs.filter((j) => j.dismissed);
  const filtered = tab === "DISMISSED" ? dismissedJobs : visibleJobs.filter((j) => j.status === tab);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <h1 className="text-2xl font-bold tracking-tight">Lavori</h1>
      <p className="mt-1 text-sm text-neutral-500">Tutti i lavori organizzati da Mastro.</p>

      <div data-testid="jobs-tabs" className="mt-5 inline-flex max-w-full flex-wrap gap-1 rounded-xl border border-neutral-300 bg-neutral-100 p-1.5">
        {TABS.map((t) => {
          const n = visibleJobs.filter((j) => j.status === t.key).length;
          return (
            <button
              key={t.key}
              data-testid={`jobs-tab-${t.key.toLowerCase()}`}
              onClick={() => setTab(t.key)}
              className={`rounded-lg px-4 py-2 text-sm transition-colors ${
                tab === t.key
                  ? "border border-neutral-300 bg-white font-semibold text-neutral-950 shadow-sm"
                  : "border border-transparent font-medium text-neutral-600 hover:bg-white/60 hover:text-neutral-900"
              }`}
            >
              {t.label}
              {t.key !== "DISMISSED" && (
                <span className={`ml-1 text-xs font-normal ${tab === t.key ? "text-teal-700" : "text-neutral-400"}`}>({n})</span>
              )}
            </button>
          );
        })}
      </div>

      {allJobs.length === 0 ? (
        <div data-testid={JOBS.listEmpty} className="mt-6 rounded-2xl border border-neutral-200 bg-white p-8 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-600">
            <MessageCircle className="h-6 w-6" />
          </span>
          <p className="mt-4 text-sm font-semibold text-neutral-950">Nessun lavoro ancora</p>
          <p className="mt-1 text-sm text-neutral-500">
            Racconta un lavoro a Mastro su WhatsApp: comparirà qui.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-neutral-200 bg-white px-4 py-10 text-center">
          <p data-testid={`jobs-empty-${tab.toLowerCase()}`} className="text-sm text-neutral-400">
            {tab === "DISMISSED" ? "Nessun lavoro eliminato." : "Nessun lavoro in questa categoria."}
          </p>
        </div>
      ) : (
        <div className="mt-6 overflow-hidden rounded-2xl border border-neutral-200 bg-white" data-testid={JOBS.list}>
          <div className="divide-y divide-neutral-100">
            {filtered.map((j) => (
              <div
                key={j.id}
                data-testid={JOBS.item}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/app/lavori/${j.id}`)}
                onKeyDown={(e) => e.key === "Enter" && navigate(`/app/lavori/${j.id}`)}
                className="flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-3.5 text-left transition-colors hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-500 sm:px-5"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-medium text-neutral-950">
                    <Briefcase className="h-4 w-4 shrink-0 text-neutral-400" />
                    <span className="truncate">{j.client || j.title}</span>
                  </div>
                  {j.description && (
                    <p className="mt-0.5 truncate text-xs text-neutral-500">{j.description}</p>
                  )}
                </div>
                <div className="ml-1 flex shrink-0 flex-wrap items-center justify-end gap-2 sm:ml-3 sm:gap-3">
                  {j.current_total != null && (
                    <span className="hidden text-sm font-semibold text-neutral-950 sm:inline">{formatEuro(j.current_total)}</span>
                  )}
                  <StatusBadge status={j.status} />
                  {!j.dismissed && j.status === "ARCHIVED" && (
                    <button
                      data-testid={JOBS.dismiss}
                      onClick={(e) => { e.stopPropagation(); setConfirmJob(j); }}
                      className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Elimina
                    </button>
                  )}
                  {j.dismissed && (
                    <button
                      data-testid={JOBS.restore}
                      disabled={restoringId === j.id}
                      onClick={(e) => { e.stopPropagation(); onRestore(j.id); }}
                      className="inline-flex items-center gap-1 rounded-lg border border-teal-200 bg-white px-2.5 py-1 text-xs font-medium text-teal-700 transition-colors hover:bg-teal-50 disabled:opacity-50"
                    >
                      {restoringId === j.id
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <RotateCcw className="h-3.5 w-3.5" />} Ripristina
                    </button>
                  )}
                  <ChevronRight className="hidden h-4 w-4 text-neutral-300 sm:block" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ConfirmDismissDialog
        open={confirmJob != null}
        onOpenChange={(o) => { if (!o && !busy) setConfirmJob(null); }}
        title={`Eliminare "${confirmJob?.client || confirmJob?.title || "questo lavoro"}"?`}
        description="Il lavoro verrà nascosto dagli elenchi. Nessun dato viene cancellato: lo ritroverai nella scheda Eliminati e potrai ripristinarlo quando vuoi."
        busy={busy}
        onConfirm={onConfirmDismiss}
        confirmTestId={JOBS.dismissConfirm}
        cancelTestId={JOBS.dismissCancel}
      />
    </motion.div>
  );
}
